/**
 * Headless-Hooks — die Primaerschnittstelle des Pakets.
 *
 * Absichtlich ohne Optik: Die beiden Bestandskunden bauen ihre Admins mit MUI,
 * kuenftige vielleicht mit etwas anderem. Jede mitgelieferte Gestaltung waere
 * dort ein Fremdkoerper, und das Modul soll sich in den Admin einfuegen, nicht
 * daneben stehen (Konzept 4.2).
 *
 * Kein React Query als Abhaengigkeit — der Zustand ist klein, und eine
 * zusaetzliche Bibliothek in jedem Kundenprojekt waere teurer als diese
 * fuenfzig Zeilen.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { BrowserHubClient, HubError, type HubClientOptions } from "../core/client.js";
import type {
  HubArticle,
  HubArticleDetail,
  HubIdee,
  HubIdeenEinstellungen,
  HubIdeenTelegram,
  HubIdeeStatus,
  HubVorschlaege,
  HubMedium,
  HubSite,
  HubAktionNeu,
  HubSocialFreigabe,
  HubSocialKalender,
  HubSocialPostNeu,
  HubTextVorschlagAnfrage,
  HubSocialKonto,
  HubKommentare,
  HubTikTokCreator,
  HubSocialPlan,
  HubSocialPostAendern,
  HubNewsletterAktion,
  HubNewsletterAusgabe,
  HubNewsletterEntwurf,
  HubNewsletterLokal,
  HubNewsletterAusgabeAendern,
  HubNewsletterThema,
  HubNewsletterThemaIn,
  HubMeineTickets,
  HubTicketErgebnis,
} from "../core/types.js";
import { kontextAufnehmen } from "../melden/index.js";
import { ERLAUBT, hochladen, vorschauErzeugen } from "./upload.js";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** true, wenn die Engine nicht erreichbar ist — der Admin bleibt benutzbar. */
  offline: boolean;
}

function messageOf(error: unknown): { message: string; offline: boolean } {
  if (error instanceof HubError) {
    return { message: error.message, offline: error.status === 503 };
  }
  return { message: error instanceof Error ? error.message : String(error), offline: true };
}

/** Wie die Bausteine den Kunden ansprechen. Standard „du“ (der Admin einer Website), im Portal „Sie“ (ab 0.17.0). */
export type HubAnrede = "du" | "sie";

interface HubKontext {
  basePath?: string;
  options?: HubClientOptions;
  anrede?: HubAnrede;
}

const HubContext = createContext<HubKontext>({});

export interface HubProviderProps {
  /** Eigene Proxy-Route, Standard `/api/content-hub`. */
  basePath?: string;
  /** Header je Anfrage — z.B. `() => ({ Authorization: \`Bearer ${getToken()}\` })`. */
  headers?: HubClientOptions["headers"];
  credentials?: RequestInit["credentials"];
  /** Standard `/hub`. Im Portal leer — dort ist `basePath` schon die Engine-Route. */
  hubPath?: string;
  /** Ansprache in Texten der Bausteine — Standard „du“. */
  anrede?: HubAnrede;
  children?: ReactNode;
}

/**
 * Wie alle Hooks und Komponenten darunter die Proxy-Route erreichen.
 *
 * Noetig nur, wenn der Admin nicht per Session-Cookie angemeldet ist. Die
 * Header-Funktion darf bei jedem Render neu entstehen: Sie wird ueber eine Ref
 * gelesen, sonst entstuende mit jedem Render ein neuer Client, jeder Hook
 * laedt neu, rendert neu — eine Schleife.
 */
export function HubProvider({ basePath, headers, credentials, hubPath, anrede, children }: HubProviderProps) {
  const eltern = useContext(HubContext);
  const aktuell = useRef(headers);
  aktuell.current = headers;
  const erbt = headers === undefined;
  const elternHeaders = eltern.options?.headers;
  const wieCookie = credentials ?? eltern.options?.credentials;
  const zwischen = hubPath ?? eltern.options?.hubPath;

  const options = useMemo<HubClientOptions>(
    () => ({
      headers: erbt ? elternHeaders : () => aktuell.current?.() ?? {},
      credentials: wieCookie,
      hubPath: zwischen,
    }),
    [erbt, elternHeaders, wieCookie, zwischen],
  );
  const wieAnreden = anrede ?? eltern.anrede;
  const value = useMemo<HubKontext>(
    () => ({ basePath: basePath ?? eltern.basePath, options, anrede: wieAnreden }),
    [basePath, eltern.basePath, options, wieAnreden],
  );
  return createElement(HubContext.Provider, { value }, children);
}

/** „du“ oder „sie“ — aus dem naechsten `HubProvider`. */
export function useAnrede(): HubAnrede {
  return useContext(HubContext).anrede ?? "du";
}

/** Proxy-Route und Client-Optionen aus dem naechsten `HubProvider` — fuer eigene Clients wie den Media-Hub (ab 0.22.0). */
export function useHubVerbindung(basePath?: string): { basePath?: string; options?: HubClientOptions } {
  const ctx = useContext(HubContext);
  return { basePath: basePath ?? ctx.basePath, options: ctx.options };
}

function useClient(basePath?: string): BrowserHubClient {
  const ctx = useContext(HubContext);
  const pfad = basePath ?? ctx.basePath;
  return useMemo(() => new BrowserHubClient(pfad, ctx.options), [pfad, ctx.options]);
}

function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({
    data: null, loading: true, error: null, offline: false,
  });
  const [nonce, setNonce] = useState(0);
  // Verhindert, dass eine langsame frueher gestartete Anfrage eine neuere
  // ueberschreibt.
  const generation = useRef(0);

  useEffect(() => {
    const current = ++generation.current;
    setState((s) => ({ ...s, loading: true }));
    loader()
      .then((data) => {
        if (current === generation.current) {
          setState({ data, loading: false, error: null, offline: false });
        }
      })
      .catch((error: unknown) => {
        if (current !== generation.current) return;
        const { message, offline } = messageOf(error);
        setState({ data: null, loading: false, error: message, offline });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { ...state, reload: useCallback(() => setNonce((n) => n + 1), []) };
}

/** Stammdaten und Kontingent der eigenen Site. */
export function useHubSite(basePath?: string) {
  const client = useClient(basePath);
  return useAsync<HubSite>(() => client.site(), [client]);
}

/** Die Warteschlange der Redaktion. */
export function useArticleQueue(status?: string, basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubArticle[]>(() => client.articles(status), [client, status]);

  const approve = useCallback(
    async (id: number, approvedBy?: string) => {
      await client.approve(id, approvedBy);
      list.reload();
    },
    [client, list],
  );

  const requestChanges = useCallback(
    async (id: number, feedback: string, permanent = false, requestedBy?: string) => {
      await client.requestChanges(id, feedback, permanent, requestedBy);
      list.reload();
    },
    [client, list],
  );

  /** Beitrag verwerfen (ab 0.21.0) — er verlaesst die Liste und erscheint nie. */
  const discard = useCallback(
    async (id: number, reason = "", discardedBy?: string) => {
      await client.discardArticle(id, reason, discardedBy);
      list.reload();
    },
    [client, list],
  );

  return { ...list, approve, requestChanges, discard };
}

/** Ein einzelner Artikel mit Text und Vorschau-Link. */
export function useArticle(id: number | null, basePath?: string) {
  const client = useClient(basePath);
  const state = useAsync<HubArticleDetail | null>(
    () => (id === null ? Promise.resolve(null) : client.article(id)),
    [client, id],
  );

  const refreshPreview = useCallback(async () => {
    if (id === null) return;
    await client.refreshPreview(id);
    // Die Vorschau entsteht asynchron in der Engine; ein kurzer Versatz beim
    // Nachladen erspart einen leeren Link.
    setTimeout(state.reload, 2000);
  }, [client, id, state]);

  return { ...state, refreshPreview };
}

/** Video-Ideen aus dem Ideen-Radar, mit Entscheidung. */
export function useIdeen(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubIdee[]>(() => client.ideen(), [client]);

  const decide = useCallback(
    async (id: number, status: Exclude<HubIdeeStatus, "proposed">, by?: string, postUrl?: string) => {
      await client.decideIdee(id, status, by, postUrl);
      list.reload();
    },
    [client, list],
  );

  return { ...list, decide };
}

/** Ausschluesse der Ideen lesen und speichern (ab 0.20.0). */
export function useIdeenEinstellungen(basePath?: string) {
  const client = useClient(basePath);
  const state = useAsync<HubIdeenEinstellungen>(() => client.ideenEinstellungen(), [client]);
  const speichern = useCallback(
    async (exclusions: string) => {
      await client.ideenEinstellungenSpeichern({ exclusions });
      state.reload();
    },
    [client, state],
  );
  return { ...state, speichern };
}

/**
 * KI-Vorschlaege ohne Vorlage (ab 0.20.0). Solange ein Lauf entsteht, wird alle
 * fuenf Sekunden nachgesehen — ohne die Liste dazwischen zu leeren.
 */
export function useVorschlaege(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubVorschlaege>(() => client.vorschlaege(), [client]);
  const laeuft = Boolean(list.data?.running);
  const { reload } = list;
  useEffect(() => {
    if (!laeuft) return;
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, [laeuft, reload]);

  const anfordern = useCallback(
    async (by?: string) => {
      await client.vorschlaegeAnfordern(by);
      reload();
    },
    [client, reload],
  );
  const decide = useCallback(
    async (id: number, status: Exclude<HubIdeeStatus, "proposed">, by?: string, postUrl?: string) => {
      await client.decideVorschlag(id, status, by, postUrl);
      reload();
    },
    [client, reload],
  );
  return { ...list, anfordern, decide };
}

/**
 * Telegram verbinden (ab 0.19.0): Verbindungen der Site, Link erzeugen, trennen.
 *
 * Der Link aus `verbinden()` wird nur einmal ausgeliefert — `link` haelt ihn,
 * bis die Seite neu geladen wird.
 */
export function useIdeenTelegram(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubIdeenTelegram[]>(() => client.ideenTelegram(), [client]);
  const [link, setLink] = useState<string | null>(null);

  const verbinden = useCallback(
    async (by?: string) => {
      const r = await client.ideenTelegramVerbinden(by);
      setLink(r.link);
      list.reload();
    },
    [client, list],
  );
  const trennen = useCallback(
    async (id: number) => {
      await client.ideenTelegramTrennen(id);
      list.reload();
    },
    [client, list],
  );

  return { ...list, link, verbinden, trennen };
}

/** Ein laufender oder abgeschlossener Upload in der Mediathek. */
export interface UploadStand {
  id: string;
  name: string;
  /** 0 bis 1 */
  anteil: number;
  fertig?: boolean;
  fehler?: string;
}

/**
 * Die Social-Mediathek: Liste, Direkt-Upload (mit Standbild), Freigabe, Loeschen.
 * Dateien gehen am Proxy vorbei direkt in den Speicher — signierte Links der Engine.
 */
export function useMediathek(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubMedium[]>(() => client.mediathek(), [client]);
  const [uploads, setUploads] = useState<UploadStand[]>([]);
  const zaehler = useRef(0);

  const hochladenDateien = useCallback(
    async (dateien: File[], by = "") => {
      const neu = dateien.map((d) => ({ id: `u${++zaehler.current}`, name: d.name, anteil: 0 }));
      setUploads((u) => [...u, ...neu]);
      const setzen = (id: string, patch: Partial<UploadStand>) =>
        setUploads((u) => u.map((x) => (x.id === id ? { ...x, ...patch } : x)));

      for (const [n, datei] of dateien.entries()) {
        const id = neu[n]!.id;
        try {
          if (!ERLAUBT.includes(datei.type)) throw new Error("Nur JPEG, PNG, WebP, MP4, MOV oder WebM");
          const v = await vorschauErzeugen(datei);
          const angelegt = await client.mediumAnlegen({
            filename: datei.name, content_type: datei.type, size_bytes: datei.size,
            width: v.width, height: v.height, duration_s: v.duration_s, by,
          });
          await hochladen(angelegt.upload_url, datei, datei.type, (a) => setzen(id, { anteil: a * 0.95 }));
          if (v.blob) await hochladen(angelegt.preview_upload_url, v.blob, "image/jpeg");
          await client.mediumFertig(angelegt.medium.id);
          setzen(id, { anteil: 1, fertig: true });
        } catch (e) {
          setzen(id, { fehler: messageOf(e).message });
        }
      }
      list.reload();
    },
    [client, list],
  );

  const aendern = useCallback(
    async (id: number, patch: { approved_for_use?: boolean; title?: string; description?: string }) => {
      await client.mediumAendern(id, patch);
      list.reload();
    },
    [client, list],
  );

  const loeschen = useCallback(
    async (id: number) => {
      await client.mediumLoeschen(id);
      list.reload();
    },
    [client, list],
  );

  /** Erledigte Uploads aus der Liste nehmen (fehlgeschlagene bleiben sichtbar). */
  const erledigteAusblenden = useCallback(() => setUploads((u) => u.filter((x) => !x.fertig)), []);

  return { ...list, uploads, hochladen: hochladenDateien, aendern, loeschen, erledigteAusblenden };
}

/** Der Social-Plan: Beitraege pruefen, anpassen, freigeben, als gepostet melden. */
export function useSocialPlaene(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubSocialPlan[]>(() => client.socialPlaene(), [client]);

  const aendern = useCallback(
    async (id: number, patch: HubSocialPostAendern) => {
      await client.socialPostAendern(id, patch);
      list.reload();
    },
    [client, list],
  );

  const freigeben = useCallback(
    async (planId: number, by = ""): Promise<HubSocialFreigabe> => {
      const ergebnis = await client.socialPlanFreigeben(planId, by);
      list.reload();
      return ergebnis;
    },
    [client, list],
  );

  const aktionAnlegen = useCallback(
    async (aktion: HubAktionNeu) => {
      await client.socialAktionAnlegen(aktion);
      list.reload();
    },
    [client, list],
  );

  const aktionLoeschen = useCallback(
    async (planId: number) => {
      await client.socialAktionLoeschen(planId);
      list.reload();
    },
    [client, list],
  );

  const loeschen = useCallback(
    async (id: number) => {
      await client.socialPostLoeschen(id);
      list.reload();
    },
    [client, list],
  );

  const gepostet = useCallback(
    async (id: number, url = "", by = "") => {
      await client.socialPostGepostet(id, url, by);
      list.reload();
    },
    [client, list],
  );

  const erneut = useCallback(
    async (id: number, trotzdem = false) => {
      await client.socialPostErneut(id, trotzdem);
      list.reload();
    },
    [client, list],
  );

  return { ...list, aendern, freigeben, gepostet, aktionAnlegen, aktionLoeschen, loeschen, erneut };
}

/** Konten bei Meta: verbinden, Seite waehlen, automatisch posten, trennen (ab 0.16.0). */
export function useSocialKonten(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubSocialKonto[]>(() => client.socialKonten(), [client]);

  /** Leitet den Browser zu Meta weiter. */
  const verbinden = useCallback(
    async (platform: string, zurueck: string, by = "") => {
      const { url } = await client.socialKontoVerbinden(platform, zurueck, by);
      window.location.assign(url);
    },
    [client],
  );

  const seite = useCallback(
    async (pageId: string) => {
      await client.socialKontoSeite(pageId);
      list.reload();
    },
    [client, list],
  );

  const autoPost = useCallback(
    async (platform: string, an: boolean) => {
      await client.socialKontoAendern(platform, an);
      list.reload();
    },
    [client, list],
  );

  const trennen = useCallback(
    async (platform: string) => {
      await client.socialKontoTrennen(platform);
      list.reload();
    },
    [client, list],
  );

  return { ...list, verbinden, seite, autoPost, trennen };
}

/**
 * Der Kalender: alle Beitraege eines Zeitraums, eigene anlegen und loeschen,
 * Texte vorschlagen lassen (ab 0.15.0). `von`/`bis` als `YYYY-MM-DD`.
 */
export function useSocialKalender(von: string, bis: string, basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubSocialKalender>(() => client.socialKalender(von, bis), [client, von, bis]);

  const anlegen = useCallback(
    async (post: HubSocialPostNeu) => {
      const neu = await client.socialPostAnlegen(post);
      list.reload();
      return neu;
    },
    [client, list],
  );

  const aendern = useCallback(
    async (id: number, patch: HubSocialPostAendern) => {
      await client.socialPostAendern(id, patch);
      list.reload();
    },
    [client, list],
  );

  const gepostet = useCallback(
    async (id: number, url = "", by = "") => {
      await client.socialPostGepostet(id, url, by);
      list.reload();
    },
    [client, list],
  );

  const loeschen = useCallback(
    async (id: number) => {
      await client.socialPostLoeschen(id);
      list.reload();
    },
    [client, list],
  );

  const vorschlag = useCallback(
    (anfrage: HubTextVorschlagAnfrage) => client.socialTextVorschlag(anfrage),
    [client],
  );

  const erneut = useCallback(
    async (id: number, trotzdem = false) => {
      await client.socialPostErneut(id, trotzdem);
      list.reload();
    },
    [client, list],
  );

  return { ...list, anlegen, aendern, gepostet, loeschen, vorschlag, erneut };
}

/** Newsletter-Themen: anlegen, aendern, pausieren, archivieren (ab 0.7.0). */
export function useNewsletterThemen(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubNewsletterThema[]>(() => client.newsletterThemen(), [client]);

  const anlegen = useCallback(
    async (thema: HubNewsletterThemaIn & { name: string }) => {
      const neu = await client.newsletterThemaAnlegen(thema);
      list.reload();
      return neu;
    },
    [client, list],
  );

  const aendern = useCallback(
    async (id: number, patch: HubNewsletterThemaIn) => {
      await client.newsletterThemaAendern(id, patch);
      list.reload();
    },
    [client, list],
  );

  const archivieren = useCallback(
    async (id: number) => {
      await client.newsletterThemaArchivieren(id);
      list.reload();
    },
    [client, list],
  );

  return { ...list, anlegen, aendern, archivieren };
}

/**
 * Newsletter-Stand der Website selbst (ab 0.8.0). `data === null` ohne Fehler
 * heisst: Diese Website versendet nicht mit dem Modul des Pakets (z.B. DGSZM
 * mit eigenem Kampagnen-System) — dann gibt es keinen Abonnenten-Reiter.
 */
export function useNewsletterLokal(basePath?: string) {
  const client = useClient(basePath);
  const state = useAsync<HubNewsletterLokal | null>(
    () =>
      client.newsletterLokal().catch((e: unknown) => {
        if (e instanceof HubError && e.status === 404) return null;
        throw e;
      }),
    [client],
  );
  const vorschau = useCallback((e: HubNewsletterEntwurf) => client.newsletterVorschau(e), [client]);
  const testmail = useCallback((e: HubNewsletterEntwurf) => client.newsletterTestmail(e), [client]);
  const abonnenten = useCallback((q = "", offset = 0) => client.newsletterAbonnenten(q, offset), [client]);
  const loeschen = useCallback(
    async (id: number) => {
      await client.newsletterAbonnentLoeschen(id);
      state.reload();
    },
    [client, state],
  );
  return { ...state, vorschau, testmail, abonnenten, loeschen };
}

/** Newsletter-Ausgaben: pruefen, bearbeiten, freigeben, zurueckziehen (ab 0.7.0). */
export function useNewsletterAusgaben(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubNewsletterAusgabe[]>(() => client.newsletterAusgaben(), [client]);

  const aendern = useCallback(
    async (id: number, patch: HubNewsletterAusgabeAendern) => {
      await client.newsletterAusgabeAendern(id, patch);
      list.reload();
    },
    [client, list],
  );

  const entscheiden = useCallback(
    async (id: number, aktion: HubNewsletterAktion, by = "", feedback = "") => {
      await client.newsletterEntscheiden(id, aktion, by, feedback);
      list.reload();
    },
    [client, list],
  );

  return { ...list, aendern, entscheiden };
}

/**
 * Eine Meldung abschicken (ab 0.12.0) — fuer einen eigenen Dialog.
 *
 * Den Zustand zum Meldezeitpunkt nimmt der Hook selbst auf; der Aufrufer muss
 * nicht wissen, was dazugehoert. `extra` ist der Platz fuer das, was nur die
 * Website weiss — etwa der Bereich, in dem gearbeitet wurde.
 */
export function useTicketMelden(basePath?: string) {
  const client = useClient(basePath);
  const [busy, setBusy] = useState(false);

  const melden = useCallback(
    async (
      titel: string,
      text: string,
      extra?: Record<string, unknown>,
    ): Promise<HubTicketErgebnis> => {
      setBusy(true);
      try {
        // `MeldeKontext` ist ein Interface ohne Index-Signatur; der Vertrag zur
        // Engine ist bewusst frei geformt. Das Ausbreiten macht daraus ein
        // Objekt, das beides erfuellt, ohne `melden/` an den Hub zu binden.
        const kontext: Record<string, unknown> = { ...kontextAufnehmen(extra) };
        return await client.ticketMelden({
          title: titel.trim(),
          body: text.trim(),
          context: kontext,
        });
      } finally {
        setBusy(false);
      }
    },
    [client],
  );

  return { melden, busy };
}

/**
 * Die eigenen Meldungen mit ihrem Stand (ab 0.13.0).
 *
 * Wird beim Oeffnen der Liste geholt — deshalb braucht die Website keinen
 * Scheduler, keinen Webhook-Endpunkt und keinen zweiten Schluessel.
 */
export function useMeineTickets(basePath?: string) {
  const client = useClient(basePath);
  return useAsync<HubMeineTickets>(() => client.meineTickets(), [client]);
}

/** Notfall-Stopp aus dem Kunden-Admin. */
export function usePause(basePath?: string) {
  const client = useClient(basePath);
  const [busy, setBusy] = useState(false);

  const setPaused = useCallback(
    async (paused: boolean, reason = "") => {
      setBusy(true);
      try {
        return await client.setPaused(paused, reason);
      } finally {
        setBusy(false);
      }
    },
    [client],
  );

  return { setPaused, busy };
}

/** Kommentare unter eigenen Beitraegen: lesen, beantworten, ausblenden (ab 0.17.0). */
export function useSocialKommentare(basePath?: string) {
  const client = useClient(basePath);
  const list = useAsync<HubKommentare>(() => client.socialKommentare(), [client]);

  const antworten = useCallback(
    async (id: number, text: string, by = "") => {
      await client.socialKommentarAntworten(id, text, by);
      list.reload();
    },
    [client, list],
  );

  const ausblenden = useCallback(
    async (id: number, hidden = true) => {
      await client.socialKommentarAusblenden(id, hidden);
      list.reload();
    },
    [client, list],
  );

  const gelesen = useCallback(
    async (ids: number[] = []) => {
      await client.socialKommentareGelesen(ids);
      list.reload();
    },
    [client, list],
  );

  const abrufen = useCallback(async () => {
    await client.socialKommentareAbrufen();
  }, [client]);

  const vorschlag = useCallback((id: number) => client.socialKommentarVorschlag(id), [client]);

  return { ...list, antworten, ausblenden, gelesen, abrufen, vorschlag };
}

/** Was das verbundene TikTok-Konto darf — fuer die Angaben am Beitrag (ab 0.18.0). */
export function useTikTokCreator(an: boolean, basePath?: string) {
  const client = useClient(basePath);
  return useAsync<HubTikTokCreator | null>(
    () => (an ? client.socialTikTokCreator() : Promise.resolve(null)),
    [client, an],
  );
}
